'use client';
import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface AppNotification {
  id: string;
  recipientRole: 'customer' | 'cleaner' | 'admin';
  recipientId: string;
  type: 'job' | 'complaint' | 'payout' | 'booking';
  title: string;
  message: string;
  isRead: boolean;
  jobId: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

function mapRow(row: any): AppNotification {
  return {
    id: row.id,
    recipientRole: row.recipient_role,
    recipientId: row.recipient_id,
    type: row.type,
    title: row.title,
    message: row.message,
    isRead: row.is_read,
    jobId: row.job_id ?? null,
    metadata: row.metadata ?? null,
    createdAt: row.created_at,
  };
}

export function useNotifications(role: 'customer' | 'cleaner' | 'admin') {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const recipientId =
    role === 'customer' ?'demo-customer-1'
      : role === 'cleaner' ?'demo-cleaner-1' :'demo-admin-1';

  const fetchNotifications = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_role', role)
        .eq('recipient_id', recipientId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError(fetchError.message);
        return;
      }
      setNotifications((data ?? []).map(mapRow));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [role, recipientId]);

  const markRead = useCallback(async (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    try {
      const supabase = createClient();
      await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', id);
    } catch {
      // revert on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: false } : n))
      );
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      const supabase = createClient();
      await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('recipient_role', role)
        .eq('recipient_id', recipientId)
        .eq('is_read', false);
    } catch {
      // silently fail — UI already updated
    }
  }, [role, recipientId]);

  const dismiss = useCallback(async (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      const supabase = createClient();
      await supabase.from('notifications').delete().eq('id', id);
    } catch {
      // silently fail
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Real-time subscription
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel(`notifications_${role}_${recipientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${recipientId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setNotifications((prev) => [mapRow(payload.new), ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setNotifications((prev) =>
              prev.map((n) => (n.id === payload.new.id ? mapRow(payload.new) : n))
            );
          } else if (payload.eventType === 'DELETE') {
            setNotifications((prev) =>
              prev.filter((n) => n.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [role, recipientId]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return { notifications, loading, error, unreadCount, markRead, markAllRead, dismiss };
}
